import React, { useEffect, useRef, useState } from "react";

import slackLogo from "./../images/slack.svg";
import teamsLogo from "./../images/microsoftteams.svg";
import asanaLogo from "./../images/asana.svg";
import jiraLogo from "./../images/jira.svg";
import linearLogo from "./../images/linear.svg";
import clickupLogo from "./../images/clickup-mark.svg";
import notionLogo from "./../images/notion-mark.svg";
import mondayLogo from "./../images/monday-mark.svg";
import plannerLogo from "./../images/microsoft-planner.svg";
import gmailLogo from "./../images/gmail.svg";
import outlookLogo from "./../images/outlook.svg";
import hubspotLogo from "./../images/hubspot-72.webp";
import pipedriveLogo from "./../images/pipedrive.svg";
import gcalLogo from "./../images/google-calendar.svg";
import githubLogo from "./../images/github.svg";

/**
 * ArchitectureFigure
 * ------------------
 * The home page's picture of BossDrop: the tools on either side, BossDrop in
 * the middle, leaders above and the team below, with what each of them gets on
 * the lines between. Pointing at, tapping or tabbing to any part lights up what
 * it connects to and says what it does. Every word is real DOM text, and the
 * figure reads fine with no interaction at all. HTML and CSS motion, no
 * dependency. Motion stops off-screen and under prefers-reduced-motion.
 *
 * The wires are drawn from the measured positions of the parts, so the same
 * markup works at both layouts (900px is the switch, here and in index.css).
 *
 * Driving it for review/screenshots:
 *   ?arch=<id>   pins one part, e.g. ?arch=decide
 *   ?motion=off  forces the still composition
 */

// `feeds` is which of the OUTPUTS a tool's signal ends up in. Facts about a
// tool follow backend/docs/AUTOMATION_ENGINE.md (the CRMs and GitHub are
// read-only there, and email is threads involving BossDrop's address).
const TOOLS = [
  {
    id: "chat",
    side: "left",
    logos: [slackLogo, teamsLogo],
    title: "Team chat",
    sub: "Slack, Microsoft Teams",
    detail:
      "Reads the channels you put in scope for blockers and decisions, and chases owners where they already talk.",
    feeds: ["brief", "escalations", "followups", "huddles"],
  },
  {
    id: "email",
    side: "left",
    logos: [gmailLogo, outlookLogo],
    title: "Email",
    sub: "Gmail, Outlook",
    detail:
      "People reply to ai@bossdrop.ai and it answers like a manager would, then folds the thread into its context.",
    feeds: ["brief", "escalations", "followups"],
  },
  {
    id: "tasks",
    side: "left",
    logos: [
      asanaLogo,
      jiraLogo,
      linearLogo,
      clickupLogo,
      notionLogo,
      mondayLogo,
      plannerLogo,
    ],
    title: "Your task tool",
    sub: "Asana, Jira, Linear, ClickUp, Notion, monday.com, Planner",
    detail:
      "Tasks, owners and due dates. It spots what slipped, then creates and updates tasks right there.",
    feeds: ["brief", "escalations", "followups", "huddles"],
  },
  {
    id: "crm",
    side: "right",
    logos: [hubspotLogo, pipedriveLogo],
    title: "Sales CRM",
    sub: "HubSpot, Pipedrive",
    detail:
      "Read-only. Open deals and their owners, so deal risk reaches the brief and the follow-ups.",
    feeds: ["brief", "followups"],
  },
  {
    id: "calendar",
    side: "right",
    logos: [gcalLogo, outlookLogo],
    title: "Calendar",
    sub: "Google Calendar, Outlook",
    detail:
      "Sees when people are available and books the decision meeting on the real calendar.",
    feeds: ["huddles"],
  },
  {
    id: "github",
    side: "right",
    logos: [githubLogo],
    title: "Code repository",
    sub: "GitHub",
    detail:
      "Read-only repo activity: stale PRs, failing checks and merges. It reads code only when an admin asks it about the code.",
    feeds: ["brief", "followups"],
  },
];

// `lights` is which parts of the figure a stage is about.
const STAGES = [
  {
    id: "detect",
    title: "Detect",
    detail:
      "Spots slipped dates, missing owners and blockers across every connected tool.",
    lights: ["tools"],
  },
  {
    id: "decide",
    title: "Decide",
    detail:
      "A planner model proposes actions from a fixed vocabulary. A critic model checks them before anything moves.",
    lights: [],
  },
  {
    id: "route",
    title: "Route",
    detail:
      "Picks the right person and channel. A guard layer blocks duplicate pings.",
    lights: ["outputs", "people"],
  },
  {
    id: "execute",
    title: "Execute",
    detail:
      "Chases, escalates, drafts and books through your real tools, on its own, inside the scope you set.",
    lights: ["tools", "outputs"],
  },
  {
    id: "audit",
    title: "Audit",
    detail: "Every action lands in an exportable log: what it did and why.",
    lights: ["tools", "outputs", "people"],
  },
];

const OUTPUTS = [
  {
    id: "brief",
    to: "leaders",
    title: "Executive brief",
    sub: "Risks, blockers, owners, decisions",
    detail:
      "Every Friday: what is at risk, who owns it and what needs a decision.",
  },
  {
    id: "escalations",
    to: "leaders",
    title: "Escalations",
    sub: "What is stuck, who needs to move",
    detail:
      "When work stays stuck, it goes to the owner's manager with the context and what is needed.",
  },
  {
    id: "followups",
    to: "team",
    title: "Follow-ups & task drafts",
    sub: "Next actions, no manual chasing",
    detail:
      "Asks owners for the update where they already work, then drafts and assigns the next task.",
  },
  {
    id: "huddles",
    to: "team",
    title: "Decision huddles",
    sub: "When a real call is needed",
    detail:
      "Books a focused meeting, with the context attached, when a real call is needed.",
  },
];

const PEOPLE = [
  {
    id: "leaders",
    count: 3,
    title: "Leaders set the rules",
    detail:
      "Set scope and rules in the Control Room, read the brief, and send any automation back to approval at any time.",
  },
  {
    id: "team",
    count: 6,
    title: "The team does the work",
    detail:
      "People answer in the tools they already use, and nobody has to chase them by hand.",
  },
];

const ALL_PARTS = [...TOOLS, ...STAGES, ...OUTPUTS, ...PEOPLE];

/* What one part lights up: the parts it connects to and the wires between. */
function traceOf(id) {
  const nodes = new Set([id]);
  const wires = new Set();
  const addTool = (t) => {
    nodes.add(t.id);
    wires.add(`tool:${t.id}`);
  };
  const addOutput = (o, toPeople = true) => {
    nodes.add(o.id);
    wires.add(`out:${o.id}`);
    if (toPeople) {
      nodes.add(o.to);
      wires.add(`to:${o.id}`);
    }
  };

  const tool = TOOLS.find((t) => t.id === id);
  if (tool) {
    addTool(tool);
    OUTPUTS.filter((o) => tool.feeds.includes(o.id)).forEach((o) => addOutput(o));
  }
  const output = OUTPUTS.find((o) => o.id === id);
  if (output) {
    addOutput(output);
    TOOLS.filter((t) => t.feeds.includes(id)).forEach(addTool);
  }
  if (PEOPLE.some((p) => p.id === id)) {
    OUTPUTS.filter((o) => o.to === id).forEach((o) => addOutput(o));
    if (id === "leaders") wires.add("rules");
  }
  const stage = STAGES.find((s) => s.id === id);
  if (stage) {
    if (stage.lights.includes("tools")) TOOLS.forEach(addTool);
    if (stage.lights.includes("outputs")) {
      OUTPUTS.forEach((o) => addOutput(o, stage.lights.includes("people")));
    }
    if (id === "audit") {
      nodes.add("foot");
      nodes.add("leaders");
      wires.add("rules");
    }
  }
  return { nodes, wires };
}

const TRACES = Object.fromEntries(ALL_PARTS.map((p) => [p.id, traceOf(p.id)]));

const n1 = (v) => Math.round(v * 10) / 10;
const curveH = (x1, y1, x2, y2) => {
  const k = (x2 - x1) / 2;
  return `M${n1(x1)} ${n1(y1)}C${n1(x1 + k)} ${n1(y1)} ${n1(x2 - k)} ${n1(y2)} ${n1(x2)} ${n1(y2)}`;
};
const curveV = (x1, y1, x2, y2) => {
  const k = (y2 - y1) / 2;
  return `M${n1(x1)} ${n1(y1)}C${n1(x1)} ${n1(y1 + k)} ${n1(x2)} ${n1(y2 - k)} ${n1(x2)} ${n1(y2)}`;
};

/* The wires, from where the parts actually are. `at` is when a wire's pulse
   leaves within the idle loop (see archRun in index.css): the signal comes in
   from the tools, passes through the five stages, then goes out to people. */
function measureWires(stage, narrow) {
  const s = stage.getBoundingClientRect();
  const box = (el) => {
    const r = el.getBoundingClientRect();
    return {
      l: r.left - s.left,
      r: r.right - s.left,
      t: r.top - s.top,
      b: r.bottom - s.top,
      cx: (r.left + r.right) / 2 - s.left,
      cy: (r.top + r.bottom) / 2 - s.top,
    };
  };
  const part = (id) => box(stage.querySelector(`[data-arch="${id}"]`));
  const face = (id, i) =>
    box(stage.querySelectorAll(`[data-arch="${id}"] .archAvatar`)[i]);
  const bar = part("bar");
  const wires = [];
  const add = (key, group, kind, d, at) =>
    wires.push({ key, group, kind, d, at });

  // Tools feed the bar
  ["left", "right"].forEach((side, sideIndex) => {
    const tools = TOOLS.filter((t) => t.side === side);
    tools.forEach((t, i) => {
      const c = part(t.id);
      const at = 200 + (sideIndex * 110 + i * 220);
      let d;
      if (!narrow) {
        const y = bar.cy + (i - (tools.length - 1) / 2) * 17;
        d =
          side === "left"
            ? curveH(c.r, c.cy, bar.l + 18, y)
            : curveH(c.l, c.cy, bar.r - 18, y);
      } else if (c.r - c.l > s.width * 0.6) {
        // A full-width chip sits on the spine: straight down from its middle.
        d = `M${n1(bar.cx)} ${n1(c.b)}L${n1(bar.cx)} ${n1(bar.t + 8)}`;
      } else {
        // Half-width chips step into the spine that runs down the middle.
        const x = c.cx < bar.cx ? c.r : c.l;
        d = `M${n1(x)} ${n1(c.cy)}Q${n1(bar.cx)} ${n1(c.cy)} ${n1(bar.cx)} ${n1(c.cy + 12)}L${n1(bar.cx)} ${n1(bar.t + 8)}`;
      }
      add(`tool:${t.id}`, `tool:${t.id}`, "tool", d, at);
    });
  });

  // Leaders' rules come straight down the middle
  if (!narrow) {
    const f = face("leaders", 1);
    add("rules", "rules", "rules", `M${n1(f.cx)} ${n1(f.b)}L${n1(f.cx)} ${n1(bar.t + 6)}`, 0);
  }

  // The bar feeds what comes out, and that reaches people
  OUTPUTS.forEach((o, i) => {
    const c = part(o.id);
    const family = OUTPUTS.filter((x) => x.to === o.to);
    const place = family.indexOf(o);
    const outAt = 3700 + i * 90;
    const toAt = 4600 + i * 90;
    if (narrow) {
      const firstTop = part(family[0].id).t;
      const forkAt = firstTop - 30;
      add(
        `out:${o.id}`,
        `out:${o.id}`,
        "out",
        `M${n1(bar.cx)} ${n1(bar.b - 8)}L${n1(bar.cx)} ${n1(forkAt)}C${n1(bar.cx)} ${n1(forkAt + 15)} ${n1(c.cx)} ${n1(firstTop - 15)} ${n1(c.cx)} ${n1(firstTop)}L${n1(c.cx)} ${n1(c.t)}`,
        outAt,
      );
      const group = part(o.to);
      add(
        `to:${o.id}`,
        `to:${o.id}`,
        "to",
        `M${n1(c.cx)} ${n1(c.b)}L${n1(c.cx)} ${n1(group.t + 4)}`,
        toAt,
      );
      return;
    }
    const above = o.to === "leaders";
    const startX = bar.cx + (c.cx - bar.cx) * 0.72;
    add(
      `out:${o.id}`,
      `out:${o.id}`,
      "out",
      above
        ? curveV(startX, bar.t + 6, c.cx, c.b)
        : curveV(startX, bar.b - 6, c.cx, c.t),
      outAt,
    );
    if (above) {
      const f = face(o.to, place === 0 ? 0 : 2);
      add(`to:${o.id}`, `to:${o.id}`, "to", curveV(c.cx, c.t, f.cx, f.b), toAt);
    } else {
      [0, 1, 2].forEach((k) => {
        const f = face(o.to, place * 3 + k);
        add(
          `to:${o.id}:${k}`,
          `to:${o.id}`,
          "to",
          curveV(c.cx, c.b, f.cx, f.t),
          toAt + k * 70,
        );
      });
    }
  });

  return { wires, width: s.width, height: s.height };
}

function readQuery() {
  if (typeof window === "undefined") return { pinned: null, motionOff: false };
  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch (e) {
    return { pinned: null, motionOff: false };
  }
  const arch = params.get("arch");
  return {
    pinned: ALL_PARTS.some((p) => p.id === arch) ? arch : null,
    motionOff: params.get("motion") === "off",
  };
}

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function PersonGlyph() {
  return (
    <svg viewBox="0 0 40 40" aria-hidden="true">
      <circle cx="20" cy="14.5" r="6.5" />
      <path d="M9 31.5a11 9.5 0 0 1 22 0z" />
    </svg>
  );
}

export default function ArchitectureFigure() {
  const [query] = useState(readQuery);
  const [pinned, setPinned] = useState(query.pinned);
  const [hovered, setHovered] = useState(null);
  const [focused, setFocused] = useState(null);
  const [reduced, setReduced] = useState(
    () => query.motionOff || prefersReducedMotion(),
  );
  const [inView, setInView] = useState(false);
  const [entered, setEntered] = useState(false);
  const [layout, setLayout] = useState({ wires: [], width: 0, height: 0 });
  const rootRef = useRef(null);
  const stageRef = useRef(null);
  const hoverOff = useRef(null);

  const active = hovered || focused || pinned;
  const trace = active ? TRACES[active] : null;

  // Draw the wires whenever the layout moves. A ResizeObserver reports right
  // after the browser's own layout, once on its own at the start, so measuring
  // there costs nothing; measuring in an effect made the browser lay out the
  // whole page on the spot (PageSpeed's forced reflow, 172 ms on the home page).
  // Watching each part as well as the stage catches the web font arriving, which
  // can change a part's height without changing the stage's.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const narrowQuery = window.matchMedia("(max-width: 900px)");
    const ro = new ResizeObserver(() =>
      setLayout(measureWires(stage, narrowQuery.matches)),
    );
    ro.observe(stage);
    stage.querySelectorAll("[data-arch]").forEach((part) => ro.observe(part));
    return () => ro.disconnect();
  }, []);

  // Respect the OS reduced-motion setting, live.
  useEffect(() => {
    if (query.motionOff || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(mq.matches);
    sync();
    if (mq.addEventListener) {
      mq.addEventListener("change", sync);
      return () => mq.removeEventListener("change", sync);
    }
    mq.addListener(sync);
    return () => mq.removeListener(sync);
  }, [query.motionOff]);

  // Don't animate off-screen. The entrance runs the first time it shows.
  useEffect(() => {
    const el = rootRef.current;
    if (!el || !("IntersectionObserver" in window)) {
      setInView(true);
      setEntered(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          setInView(e.isIntersecting);
          if (e.isIntersecting) setEntered(true);
        }),
      { threshold: 0.08 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // A tap anywhere that isn't a part lets go of the pinned one. Pointerup, not
  // click: iOS sends no click for a tap on plain page, and a touch that turns
  // into a scroll ends in pointercancel, so scrolling doesn't clear it.
  useEffect(() => {
    if (!pinned) return;
    const onUp = (e) => {
      if (!e.target.closest || !e.target.closest("[data-arch-part]")) {
        setPinned(null);
      }
    };
    document.addEventListener("pointerup", onUp);
    return () => document.removeEventListener("pointerup", onUp);
  }, [pinned]);

  useEffect(() => () => window.clearTimeout(hoverOff.current), []);

  const bind = (id) => ({
    "data-arch": id,
    "data-arch-part": "",
    "aria-pressed": pinned === id,
    "aria-describedby": `arch-detail-${id}`,
    onClick: () => setPinned((p) => (p === id ? null : id)),
    onPointerEnter: (e) => {
      if (e.pointerType === "touch") return;
      window.clearTimeout(hoverOff.current);
      setHovered(id);
    },
    onPointerLeave: (e) => {
      if (e.pointerType === "touch") return;
      // A short grace period, so crossing the gap to the next part doesn't
      // flash the whole figure back on.
      hoverOff.current = window.setTimeout(() => setHovered(null), 130);
    },
    // Only keyboard focus. A mouse click or a tap is already handled above.
    onFocus: (e) => {
      if (e.currentTarget.matches(":focus-visible")) setFocused(id);
    },
    onBlur: () => setFocused(null),
  });

  const lit = (id, base) =>
    [
      base,
      active === id ? "isActive" : "",
      trace && trace.nodes.has(id) ? "isLit" : "",
    ]
      .filter(Boolean)
      .join(" ");

  const clear = (e) => {
    if (e.key === "Escape") {
      setPinned(null);
      setFocused(null);
      setHovered(null);
    }
  };

  const cls = [
    "archFigure",
    reduced ? "isStatic" : "",
    entered ? "isEntered" : "",
    inView ? "" : "isOffscreen",
    active ? "hasActive" : "",
    STAGES.some((st) => st.id === active) ? "hasStage" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const renderTool = (t) => (
    <li key={t.id} className={`archToolItem archTool-${t.id}`}>
      <button type="button" className={lit(t.id, "archChip")} {...bind(t.id)}>
        <span className={t.logos.length > 2 ? "archLogos archLogosRow" : "archLogos"}>
          {t.logos.map((src, i) => (
            <img
              key={i}
              src={src}
              alt=""
              width="18"
              height="18"
              loading="lazy"
              decoding="async"
            />
          ))}
        </span>
        <strong>{t.title}</strong>
        <span className="archSub">{t.sub}</span>
      </button>
    </li>
  );

  const renderGroup = (p, label) => (
    <div className={`archGroup archGroup-${p.id}`}>
      <ul className="archOutputs" aria-label={label}>
        {OUTPUTS.filter((o) => o.to === p.id).map((o) => (
          <li key={o.id}>
            <button
              type="button"
              className={lit(o.id, "archChip archChipOut")}
              {...bind(o.id)}
            >
              <strong>{o.title}</strong>
              <span className="archSub">{o.sub}</span>
            </button>
          </li>
        ))}
      </ul>
      <button type="button" className={lit(p.id, "archPeople")} {...bind(p.id)}>
        <span className="archFaces" aria-hidden="true">
          {Array.from({ length: p.count }, (_, i) => (
            <span
              key={i}
              className="archAvatar"
              style={{ "--face": i }}
            >
              <PersonGlyph />
            </span>
          ))}
        </span>
        <strong>{p.title}</strong>
      </button>
    </div>
  );

  return (
    <figure className={cls} ref={rootRef} onKeyDown={clear}>
      <div className="archStage" ref={stageRef}>
        <svg
          className="archWires"
          aria-hidden="true"
          width={layout.width}
          height={layout.height}
          viewBox={`0 0 ${layout.width || 1} ${layout.height || 1}`}
        >
          <defs>
            <linearGradient
              id="archLine"
              gradientUnits="userSpaceOnUse"
              x1="0"
              y1="0"
              x2={layout.width || 1}
              y2="0"
            >
              <stop offset="0" stopColor="#8fd8f0" />
              <stop offset="0.5" stopColor="#b7a4e6" />
              <stop offset="1" stopColor="#8f7fd0" />
            </linearGradient>
          </defs>
          {layout.wires.map((w) => (
            <path
              key={w.key}
              d={w.d}
              stroke={w.kind === "tool" ? undefined : "url(#archLine)"}
              className={[
                "archWire",
                `archWire-${w.kind}`,
                trace && trace.wires.has(w.group) ? "isLit" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            />
          ))}
        </svg>

        {!reduced && (
          <div className="archPulses" aria-hidden="true">
            {layout.wires.map((w, i) => (
              <i
                key={w.key}
                className={
                  trace && trace.wires.has(w.group)
                    ? "archPulse isLit"
                    : "archPulse"
                }
                style={{
                  offsetPath: `path("${w.d}")`,
                  "--at": `${w.at}ms`,
                  "--lit-at": `${(i % 6) * 110}ms`,
                }}
              />
            ))}
          </div>
        )}

        <ul className="archTools archToolsLeft" aria-label="Tools it works in">
          {TOOLS.filter((t) => t.side === "left").map(renderTool)}
        </ul>
        <ul className="archTools archToolsRight" aria-label="More tools it works in">
          {TOOLS.filter((t) => t.side === "right").map(renderTool)}
        </ul>

        <div className="archCoreWrap">
          <i className="archGlow" aria-hidden="true" />
          <div className="archCore" data-arch="bar">
            <i className="archSheen" aria-hidden="true" />
            <h3 className="archCoreName">BossDrop AI</h3>
            <ol className="archStages" aria-label="What it does, in order">
              {STAGES.map((st, i) => (
                <li key={st.id}>
                  <button
                    type="button"
                    className={lit(st.id, "archStep")}
                    style={{ "--step": i }}
                    {...bind(st.id)}
                  >
                    <span>{st.title}</span>
                  </button>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* Every line stays in the DOM and in one grid cell: the cell keeps the
            height of the longest, so nothing moves when the text changes, and
            each button's aria-describedby points at its own line. */}
        <div className="archReadout">
          <p className={active ? "" : "isOn"}>
            <strong>The work a coordination role does all day.</strong> It used
            to be a headcount line. Now it runs on AI.{" "}
            <span className="archHint">
              Hover or tap any part to see what it connects to.
            </span>
          </p>
          {ALL_PARTS.map((p) => (
            <p
              key={p.id}
              id={`arch-detail-${p.id}`}
              className={active === p.id ? "isOn" : ""}
            >
              <strong>{p.title}.</strong> {p.detail}
            </p>
          ))}
        </div>

        <div className="archBranches">
          {renderGroup(PEOPLE[0], "What leaders get")}
          {renderGroup(PEOPLE[1], "What the team gets")}
        </div>
      </div>

      <div className="archGone">
        <p className="archGoneHead">Off your plate</p>
        <ul>
          <li>
            <s>Status meetings</s>
          </li>
          <li>
            <s>Chasing updates</s>
          </li>
          <li>
            <s>Weekly roll-up chase</s>
          </li>
        </ul>
      </div>

      <figcaption className={lit("foot", "archFoot")}>
        Coordination now runs on AI. Approval wherever you want it, every action
        logged.
      </figcaption>
    </figure>
  );
}
